import { CommonModule } from '@angular/common';
import {
  Component,
  Output,
  EventEmitter,
  Input,
  HostListener,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone
} from '@angular/core';
import {
  BodyPainColor,
  BodyPart,
  BodyPartLabel,
  BODY_PARTS
} from '../../data/models/body-parts.model';
import { TranslatePipe } from '../../data/pipes/translate.pipe';
import interact from 'interactjs';

@Component({
  selector: 'app-body-figure',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './body-figure.component.html',
  styleUrls: ['./body-figure.component.scss']
})
export class BodyFigureComponent implements OnInit, AfterViewInit {
  @Input() refilledBodyParts: Record<BodyPartLabel, BodyPart> | null = null;
  @Output() bodyPartSelected = new EventEmitter<BodyPartLabel | null>();
  @Output() bodyPainChanged = new EventEmitter<Record<BodyPartLabel, BodyPart>>();

  @ViewChild('painIndicator', { static: false }) painIndicatorRef!: ElementRef;
  @ViewChild('painSelector', { static: false }) painSelectorRef!: ElementRef;

  @HostListener('window:resize')
  onResize(): void {
    this.screenWidth = window.innerWidth;
    this.updateTurnBodyVisibility();
  }

  bodyPartPainLevel = -1;
  bodyPartLabels = BodyPartLabel;
  painColors = Object.values(BodyPainColor);
  selectedBodyPart: BodyPartLabel | null = null;
  showBothBodySides = true;
  currentBodySide: 'front' | 'back' = 'front';
  screenWidth = window.innerWidth;
  isRotating = false;
  isDragging = false;
  bodyParts: Record<BodyPartLabel, BodyPart> = { ...BODY_PARTS };

  get painLevelLabel(): string {
    if (!this.selectedBodyPart) {
      return 'WebApp.v1.Survey.SurveyBody.PainLevel.NoneSelected';
    }

    return ('WebApp.v1.SurveyBody.BodyPart.' + this.selectedBodyPart);
  }

  get painLevelSubTitle(): string {
    if (!this.selectedBodyPart) {
      return 'WebApp.v1.Survey.SurveyBody.PainLevel.ClickTheBodyParts';
    }

    if (this.bodyParts[this.selectedBodyPart].painLevel === -1) {
      return 'WebApp.v1.Survey.SurveyBody.PainLevel.IndicateDegreeOfPain';
    }

    return 'WebApp.v1.SurveyBody.PainLevel.Pain' + this.bodyParts[this.selectedBodyPart].painLevel;
  }

  constructor(private zone: NgZone) {}

  ngOnInit(): void {
    this.updateTurnBodyVisibility();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.tryInitDrag(), 0);
  }

  private tryInitDrag(): void {
    const indicatorEl = this.painIndicatorRef?.nativeElement as HTMLElement | undefined;
    const selectorEl = this.painSelectorRef?.nativeElement as HTMLElement | undefined;

    if (!indicatorEl || !selectorEl) {
      return;
    }

    this.initializeDrag(indicatorEl, selectorEl);
  }

  private initializeDrag(indicatorEl: HTMLElement, selectorEl: HTMLElement): void {
    let isHorizontal = this.screenWidth < 1400;
    let sectionSize = 0;
    let minTop = 15;
    let maxTop = 15;
    let maxLeft = 0;
    let currentTop = parseFloat(indicatorEl.style.top || `${minTop}`);
    let currentLeft = parseFloat(indicatorEl.style.left || '0');
    let rafId: number | null = null;
    let scheduled = false;

    const writePosition = (): void => {
      scheduled = false;
      if (isHorizontal) {
        indicatorEl.style.left = `${currentLeft}px`;
      } else {
        indicatorEl.style.top = `${currentTop}px`;
      }
      rafId = null;
    };

    const scheduleWrite = (): void => {
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(writePosition);
    };

    const calculateBoundaries = (): void => {
      isHorizontal = this.screenWidth <= 1400;

      if (isHorizontal) {
        const selectorWidth = selectorEl.offsetWidth;
        sectionSize = selectorWidth / 10;
        maxLeft = Math.max(0, selectorWidth - sectionSize);
        currentLeft = parseFloat(indicatorEl.style.left || '0');
      } else {
        const selectorHeight = selectorEl.offsetHeight;
        sectionSize = selectorHeight / 10;
        minTop = 15;
        maxTop = 15 + sectionSize * 9;
        currentTop = parseFloat(indicatorEl.style.top || `${minTop}`);
      }
    };

    const handleDragStart = (): void => {
      this.isDragging = true;
      indicatorEl.style.transition = 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out';

      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      scheduled = false;
      calculateBoundaries();
    };

    const handleDragMove = (event: { dx: number; dy: number }): void => {
      if (isHorizontal) {
        currentLeft = Math.max(0, Math.min(currentLeft + event.dx, maxLeft));
      } else {
        currentTop = Math.max(minTop, Math.min(currentTop + event.dy, maxTop));
      }
      scheduleWrite();
    };

    const calculatePainLevel = (): number => {
      if (isHorizontal) {
        return Math.min(10, Math.max(1, Math.round(currentLeft / sectionSize) + 1));
      } else {
        const fromTop = currentTop - minTop;

        return Math.min(10, Math.max(1, 10 - Math.round(fromTop / sectionSize)));
      }
    };

    const snapToLevel = (level: number): void => {
      if (isHorizontal) {
        currentLeft = (level - 1) * sectionSize;
      } else {
        currentTop = minTop + (10 - level) * sectionSize;
      }
    };

    const handleDragEnd = (): void => {
      if (!this.selectedBodyPart) {
        return;
      }

      const level = calculatePainLevel();

      if (rafId) cancelAnimationFrame(rafId);

      snapToLevel(level);
      writePosition();

      this.onPainLevelSelected(level);

      setTimeout(() => {
        this.isDragging = false;
      }, 50);
    };

    interact(indicatorEl).draggable({
      inertia: false,
      listeners: {
        start: handleDragStart,
        move: handleDragMove,
        end: handleDragEnd
      }
    });
  }

  updatePainIndicatorPosition(painLevel: number, isPainLevelSelected: boolean): void {
    const isHorizontal = this.screenWidth < 1400;
    const dimension = isHorizontal ? 'Width' : 'Height';
    const selectorSize = this.painSelectorRef?.nativeElement?.[`offset${dimension}`];

    if (!selectorSize) {
      return;
    }

    setTimeout(() => {
      const indicator = this.painIndicatorRef?.nativeElement;

      if (!indicator) {
        return;
      }

      const sectionSize = selectorSize / 10;
      const padding = 3;

      if (isHorizontal) {
        indicator.style.width = `${sectionSize - padding}px`;
        indicator.style.top = '8px';
        indicator.style.left = `${sectionSize * (painLevel - 1)}px`;
      } else {
        indicator.style.height = `${sectionSize - padding}px`;
        indicator.style.top = `${15 + sectionSize * (10 - painLevel) - 0.5}px`;
      }

      if (isPainLevelSelected) {
        indicator.style.transition = `left 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          top 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.3s ease-in-out,
          visibility 0.3s ease-in-out`;
      }
    }, 0);
  }

  onBodyPartClick(bodyPartKey: BodyPartLabel): void {
    if (this.selectedBodyPart && this.selectedBodyPart !== bodyPartKey) {
      this.bodyParts[this.selectedBodyPart].color = BodyPainColor.NOPAIN;
    }

    if (this.selectedBodyPart === bodyPartKey) {
      this.selectedBodyPart = null;
      this.bodyPartSelected.emit(null);
      this.bodyPartPainLevel = -1;

      if (this.bodyParts[bodyPartKey].painLevel === -1) {
        this.bodyParts[bodyPartKey].color = BodyPainColor.NOPAIN;
      }
    } else {
      if (this.selectedBodyPart) {
        const painLevel = this.bodyParts[this.selectedBodyPart].painLevel;

        if (painLevel === -1) {
          this.bodyParts[this.selectedBodyPart].color = BodyPainColor.NOPAIN;
        } else {
          this.bodyParts[this.selectedBodyPart].color =  BodyPainColor[`LEVEL_${painLevel}` as keyof typeof BodyPainColor];
        }
      }

      this.selectedBodyPart = bodyPartKey;
      this.bodyPartPainLevel = this.bodyParts[bodyPartKey].painLevel;
      this.bodyPartSelected.emit(bodyPartKey);

      if (this.bodyParts[bodyPartKey].painLevel === -1) {
        this.bodyParts[bodyPartKey].color = BodyPainColor.NO_PAIN_SELECTED;
        this.bodyPartPainLevel = -1;
      }

      this.updatePainIndicatorPosition(this.bodyPartPainLevel, false);
      setTimeout(() => this.tryInitDrag(), 0);
    }
  }

  onPainLevelSelected(painLevel: number): void {
    if (!this.selectedBodyPart) {
      return;
    }

    this.bodyPartPainLevel = painLevel;
    const bodyPart = this.bodyParts[this.selectedBodyPart];
    const isPainLevelSelected = this.bodyParts[this.selectedBodyPart].painLevel > -1;

    if (bodyPart.painLevel === painLevel) {
      bodyPart.painLevel = -1;
      bodyPart.color = BodyPainColor.NOPAIN;
      this.bodyPartPainLevel = -1;
    } else {
      bodyPart.painLevel = painLevel;
      bodyPart.color = BodyPainColor[`LEVEL_${painLevel}` as keyof typeof BodyPainColor];
    }

    this.bodyPainChanged.emit(this.bodyParts);
    this.updatePainIndicatorPosition(painLevel, isPainLevelSelected);
  }

  onPainIndicatorClick(): void {
    if (this.isDragging || !this.selectedBodyPart) {
      return;
    }

    const bodyPart = this.bodyParts[this.selectedBodyPart];

    if (this.bodyPartPainLevel === bodyPart.painLevel && bodyPart.painLevel > -1) {
      this.painIndicatorRef.nativeElement.style.transition = 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out';

      bodyPart.painLevel = -1;
      bodyPart.color = BodyPainColor.NOPAIN;
      this.bodyPartPainLevel = -1;

      this.bodyPainChanged.emit(this.bodyParts);
      this.updatePainIndicatorPosition(-1, false);
    }
  }

  onTurnBody(): void {
    if (this.isRotating) {
      return;
    }

    this.isRotating = true;
    const animationDuration = 250;
    const timeout = animationDuration / 2;

    setTimeout(() => {
      this.currentBodySide = this.currentBodySide === 'front' ? 'back' : 'front';
      this.isRotating = false;
    }, timeout);
  }

  resetAllBodyParts(): void {
    this.selectedBodyPart = null;
    this.bodyPartSelected.emit(null);
    Object.keys(this.bodyParts).forEach(key => {
      const bodyPartKey = key as BodyPartLabel;
      this.bodyParts[bodyPartKey] = {
        painLevel: -1,
        color: BodyPainColor.NOPAIN
      };
    });
  }

  refillBodyParts(refilledBodyParts: Record<BodyPartLabel, BodyPart>): void {
    this.selectedBodyPart = null;
    this.bodyPartSelected.emit(null);
    Object.keys(refilledBodyParts).forEach(key => {
      this.bodyParts[key as BodyPartLabel] = refilledBodyParts[key as BodyPartLabel];
    });
  }

  saveBodyPartPainClick(): void {
    if (!this.selectedBodyPart) {
      return;
    }

    this.bodyParts[this.selectedBodyPart].painLevel = this.bodyPartPainLevel;

    if (this.bodyPartPainLevel === -1) {
      this.bodyParts[this.selectedBodyPart].color = BodyPainColor.NOPAIN;
    } else {
      this.bodyParts[this.selectedBodyPart].color = BodyPainColor[`LEVEL_${this.bodyPartPainLevel}` as keyof typeof BodyPainColor];
    }

    this.bodyPainChanged.emit(this.bodyParts);
    this.selectedBodyPart = null;
    this.bodyPartSelected.emit(null);
    this.bodyPartPainLevel = -1;
  }

  nextBodyPart(): void {
    if (!this.selectedBodyPart) {
      this.selectFirstBodyPart();

      return;
    }

    this.updateCurrentBodyPart();
    const nextBodyPartKey = this.getNextBodyPartKey();
    this.selectNextBodyPart(nextBodyPartKey);
  }

  private selectFirstBodyPart(): void {
    const bodyPartKey = Object.keys(this.bodyParts)[0] as BodyPartLabel;
    const bodyPart = this.bodyParts[bodyPartKey];

    this.updateBodyPartColor(bodyPart, true);
    this.selectedBodyPart = bodyPartKey;
    this.emitBodyPartChanges(bodyPartKey);
  }

  private updateCurrentBodyPart(): void {
    if (!this.selectedBodyPart) {
      return;
    }

    const bodyPart = this.bodyParts[this.selectedBodyPart];
    this.updateBodyPartColor(bodyPart, false);
  }

  private getNextBodyPartKey(): BodyPartLabel {
    if (!this.selectedBodyPart) {
      return Object.keys(this.bodyParts)[0] as BodyPartLabel;
    }

    const bodyPartIndex = Object.keys(this.bodyParts).indexOf(this.selectedBodyPart);
    let nextBodyPartKey = Object.keys(this.bodyParts)[bodyPartIndex + 1] as BodyPartLabel;

    if (!this.bodyParts[nextBodyPartKey]) {
      if (!this.showBothBodySides) {
        this.onTurnBody();
      }

      nextBodyPartKey = Object.keys(this.bodyParts)[0] as BodyPartLabel;
    }

    return nextBodyPartKey;
  }

  private selectNextBodyPart(bodyPartKey: BodyPartLabel): void {
    const nextBodyPart = this.bodyParts[bodyPartKey];
    this.updateBodyPartColor(nextBodyPart, true);
    this.selectedBodyPart = bodyPartKey;
    this.emitBodyPartChanges(bodyPartKey);
  }

  private updateBodyPartColor(bodyPart: BodyPart, isSelected: boolean): void {
    if (bodyPart.painLevel === -1) {
      bodyPart.color = isSelected ? BodyPainColor.NO_PAIN_SELECTED : BodyPainColor.NOPAIN;
      this.bodyPartPainLevel = -1;
    } else {
      const currentPainLevel = bodyPart.painLevel;
      bodyPart.color = BodyPainColor[`LEVEL_${currentPainLevel}` as keyof typeof BodyPainColor];
      this.bodyPartPainLevel = currentPainLevel;
    }
  }

  private emitBodyPartChanges(bodyPartKey: BodyPartLabel): void {
    this.bodyPartSelected.emit(bodyPartKey);
    this.bodyPainChanged.emit(this.bodyParts);
  }

  private updateTurnBodyVisibility(): void {
    this.showBothBodySides = this.screenWidth > 1400;

    if (this.showBothBodySides) {
      this.currentBodySide = 'front';
    }
  }
}
